import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retry, tap } from 'rxjs/operators';
import { ShardHealthService } from '../services/shard-health.service';
import { parseReadOnlyTokens, parseRetryAfter, parseSites, scopeFrom503, toShardNumber } from '../helpers/shard-health-parsers';

/** Per-retry foreground cap for GET auto-retry delays (seconds). Bounds each individual wait. */
const MAX_FOREGROUND_RETRY_SECONDS = 5;
/** Number of automatic retries for idempotent (GET) requests. Writes are never auto-retried. */
const GET_RETRY_COUNT = 2;

/**
 * Reacts to the API's "shard health" contract:
 *
 * - Reads `X-Shard-Unavailable` / `X-Shard-ReadOnly` headers on any `200` and feeds
 *   {@link ShardHealthService}. `X-Shard-ReadOnly` mixes site `SH`s with the global scopes
 *   (`"2,common"`); both halves are marked. (Requires the API to expose these via CORS
 *   `Access-Control-Expose-Headers`; if `headers.get(...)` is null in the browser while the
 *   header shows in the Network tab, that CORS change is missing on the backend.)
 * - Detects the `unavailable` array in **partial `POST /measurements` `200`** responses
 *   (a `200` that is really a partial success — easy to miss if you only check the status).
 * - Auto-retries **GET** `503`s up to {@link GET_RETRY_COUNT} times, backing off across attempts from
 *   `Retry-After` but capping **each** wait at {@link MAX_FOREGROUND_RETRY_SECONDS}. Note the cap is
 *   per-retry, not a total budget: worst case is `GET_RETRY_COUNT × MAX_FOREGROUND_RETRY_SECONDS`
 *   (~10 s) of added latency before the `503` surfaces. A **total outage** (`scope: 'cluster'`) is not
 *   retried at all — nothing responds, so the waits would only make the whole app sluggish.
 * - Branches the `503` on its `scope` — per-site (`shard`) marks are optimistic and instant, a
 *   `cluster` scope marks a total outage, and a `503` with no `scope` (proxy / load balancer / deploy)
 *   is left alone — and **re-throws it** so existing/caller error handling still runs (write flows show
 *   a manual "Reintentar" toast). Note that "not marked" and "not retried" are **different** predicates:
 *   a scope-less `503` marks nothing but is still retried, because a deploy window or a load balancer
 *   blipping is precisely the case a short retry rescues.
 *
 * Registered **last** in the interceptor chain (after auth/error/version) so its GET retries
 * do not re-trigger the global error interceptor. The `ErrorInterceptor` is adjusted to let
 * `503` pass through instead of showing its generic error toast. Transparent when no
 * `X-Shard-*` header and no `503` are present.
 */
@Injectable()
export class ShardHealthInterceptor implements HttpInterceptor {
  constructor(private shard: ShardHealthService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Never run shard-health logic on the authoritative poll itself. This interceptor processes
    // responses *before* the ErrorInterceptor, so a `503` from the poll would trigger another
    // refresh here → infinite loop. Let it pass straight through; the poll pipeline handles its
    // own errors (see ShardHealthService.startPolling / ApiService.getShardStatus).
    if (request.url.includes('/system-health/shard-status')) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      tap((event) => {
        if (!(event instanceof HttpResponse)) {
          return;
        }
        const retryAfter = parseRetryAfter(event.headers.get('Retry-After'));
        const unavailableSites = parseSites(event.headers.get('X-Shard-Unavailable'));
        // X-Shard-ReadOnly mixes site SHs with the global scopes (common/cluster); split them.
        const readOnly = parseReadOnlyTokens(event.headers.get('X-Shard-ReadOnly'));
        this.shard.reportUnavailable(unavailableSites, retryAfter);
        this.shard.reportReadOnly(readOnly.shs, retryAfter);
        // A named global scope (common/cluster) has no per-SH set to mark, so it gets a synthesised
        // read-only entry of its own — the header means that scope is served from a secondary, so its
        // data may be frozen and writes to it will 503. The authoritative poll then confirms or clears it.
        readOnly.scopes.forEach((scope) => this.shard.reportGlobalReadOnly(scope, retryAfter));
        let sawSignal = unavailableSites.length > 0 || readOnly.shs.length > 0 || readOnly.scopes.length > 0;

        // Partial POST /measurements: HTTP 200 but some rows rejected by a downed site.
        // Scoped to measurement writes so an `unavailable` field on any unrelated 200 body
        // is never mistaken for a shard-health signal.
        if (request.method === 'POST' && request.url.includes('/measurements')) {
          const unavailable = (event.body as any)?.unavailable;
          if (Array.isArray(unavailable) && unavailable.length) {
            // toShardNumber (not `+entry?.SH`) so a null/'' SH is dropped instead of coercing to a
            // phantom "Sitio 0" — same guard the header/503 parsers use.
            const sites = unavailable.map((entry: any) => toShardNumber(entry?.SH)).filter((sh): sh is number => sh !== null);
            this.shard.reportUnavailable(sites, retryAfter);
            sawSignal = sawSignal || sites.length > 0;
          }
        }

        // A degradation signal is an optimistic, instant mark; trigger a throttled authoritative
        // poll so the state is confirmed or cleared within seconds.
        if (sawSignal) {
          this.shard.requestRefresh();
        }
      }),
      retry({
        count: request.method === 'GET' ? GET_RETRY_COUNT : 0,
        delay: (error, retryCount) => {
          if (!(error instanceof HttpErrorResponse) || error.status !== 503) {
            // Only 503 is retried; anything else propagates immediately.
            return throwError(() => error);
          }
          // Mark on FIRST sight, not once the retries are exhausted: otherwise the advisory arrives
          // GET_RETRY_COUNT waits late (~10 s) even though we already know. The retries carry on trying
          // to serve the request; the mark is idempotent and the poll confirms or clears it.
          this.markFrom503(error);
          // Total outage: nothing responds, and it will not recover within the foreground budget.
          // Fail fast instead of adding GET_RETRY_COUNT waits to every request in the app (the global
          // advisory explains what is happening, and the poll keeps watching for recovery).
          if (scopeFrom503(error.error) === 'cluster' || this.shard.clusterUnavailable()) {
            return throwError(() => error);
          }
          const retryAfter = parseRetryAfter(error.headers.get('Retry-After'));
          // Back off across attempts (`retryCount` is 1-based) while still capping each individual wait.
          const seconds = Math.min((retryAfter ?? MAX_FOREGROUND_RETRY_SECONDS) * retryCount, MAX_FOREGROUND_RETRY_SECONDS);
          return timer(seconds * 1000);
        },
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 503) {
          // Already marked from `delay` on a retried GET; idempotent, and the only path for writes
          // (which are never retried, so `delay` never runs for them).
          this.markFrom503(error);
        }
        // The 503 keeps propagating so callers / the error interceptor can handle it.
        return throwError(() => error);
      }),
    );
  }

  /**
   * Hand the `503` to the service, which owns the `scope` rule (shared with its own poll's failure path),
   * and ask for an authoritative confirmation. Idempotent: the per-SH sets only emit when membership
   * changes and the global marks just re-arm their TTL, so calling this once per retry attempt is harmless.
   */
  private markFrom503(error: HttpErrorResponse): void {
    this.shard.reportFrom503(error.error, parseRetryAfter(error.headers.get('Retry-After')));
    // Optimistic instant mark; confirm/clear it via a throttled authoritative poll.
    this.shard.requestRefresh();
  }
}
